import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import {
  BottomSheet,
  Illustration,
  Text,
  TextButton,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { Link } from "react-router-dom";

import { type PlacementCandidate } from "../../PlacementCandidate/types";
import { type PlacementWithDetails } from "../api/useFetchPaginatedPlacements";

interface ExternalApplicationDialogProps {
  modalState: UseModalState;
  placement: PlacementWithDetails;
  placementCandidate: PlacementCandidate;
  isApplying: boolean;
  onApply: () => Promise<void>;
  onClose: () => void;
}

export function ExternalApplicationDialog(props: ExternalApplicationDialogProps) {
  const { modalState, placement, placementCandidate, isApplying, onApply, onClose } = props;

  const eventContext = {
    placement,
    placementCandidate,
  };

  const handleApply = async () => {
    await onApply();
    logEvent(APP_V2_APP_EVENTS.EXTERNAL_APPLICATION_DIALOG_APPLIED, eventContext);
    modalState.closeModal();
  };

  const handleDidNotApply = () => {
    onClose();
    logEvent(APP_V2_APP_EVENTS.EXTERNAL_APPLICATION_DIALOG_DID_NOT_APPLY, eventContext);
    modalState.closeModal();
  };

  const handleClose = () => {
    modalState.closeModal();
  };

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={handleClose} />}
      footer={
        <DialogFooter orientation="horizontal" sx={{ px: 8, pb: 8, pt: 2 }}>
          <Stack spacing={6} width="100%">
            <LoadingButton
              variant="contained"
              size="large"
              isLoading={isApplying}
              onClick={handleApply}
            >
              I applied
            </LoadingButton>
            <TextButton
              LinkComponent={Link}
              size="large"
              disabled={isApplying}
              sx={{ alignSelf: "center" }}
              onClick={handleDidNotApply}
            >
              I didn&apos;t apply
            </TextButton>
          </Stack>
        </DialogFooter>
      }
    >
      <Box sx={{ px: 5, py: 6 }}>
        <Stack
          spacing={8}
          alignItems="center"
          justifyContent="center"
          sx={{ paddingX: 6, paddingTop: 6, paddingBottom: 10 }}
        >
          <Illustration type="document-approved-warm" />
          <Text variant="h2" textAlign="center">
            Did you apply to {placement.workplace.name}?
          </Text>
        </Stack>
      </Box>
    </BottomSheet>
  );
}
